import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import TextField from "./components/TextField";
import Flag from "./components/Flag";
import Random_flag from "./helper/random_flag";
import useDebounce from "./hooks/useDebounce";
import Github from "./components/Github";

const MatrixBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const chars = "01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲンABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>{}[]!@#$%^&*";
    const charArray = chars.split("");
    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);
    const drops: number[] = Array(columns).fill(1);

    const draw = () => {
      ctx.fillStyle = "rgba(10, 10, 10, 0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "#00ff00";
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        const text = charArray[Math.floor(Math.random() * charArray.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        ctx.fillStyle = `rgba(0, 255, 0, ${Math.random() * 0.5 + 0.2})`;
        ctx.fillText(text, x, y);

        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };

    const interval = setInterval(draw, 50);

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return <Canvas ref={canvasRef} />;
};

const App: React.FC = () => {
  const [value, setValue] = useState<string>("");
  const [prefix, setPrefix] = useState<string>("flag");
  const [flag, setFlag] = useState<string>("");
  const debouncedValue = useDebounce(value, 500);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrefix(e.target.value);
  };

  const handleReset = () => {
    setFlag(Random_flag(debouncedValue));
  };

  useEffect(() => {
    setFlag(Random_flag(debouncedValue));
  }, [debouncedValue]);

  return (
    <Container>
      <MatrixBackground />
      <Overlay />
      <Content>
        <Header>CTF Flag Generator</Header>
        <InputSection>
          <TextField
            value={prefix}
            onChange={handlePrefixChange}
            label="Prefix"
            placeholder="flag"
          />
          <TextField
            value={value}
            onChange={handleChange}
            label="Flag Content"
            placeholder="Enter text here..."
          />
        </InputSection>
        <Flag value={flag} prefix={prefix} onReset={handleReset} />
      </Content>
      <Github />
    </Container>
  );
};

export default App;

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(ellipse at center, rgba(10, 10, 10, 0.3) 0%, rgba(10, 10, 10, 0.8) 100%);
  z-index: 1;
  pointer-events: none;
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: 100%;
  max-width: 500px;
  position: relative;
  z-index: 2;
  background: rgba(10, 10, 10, 0.9);
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 255, 0, 0.3);
  box-shadow: 0 0 40px rgba(0, 255, 0, 0.1);
`;

const Header = styled.h1`
  font-size: clamp(0.8rem, 3vw, 1.2rem);
  color: #00ff00;
  text-align: center;
  text-shadow: 0 0 20px rgba(0, 255, 0, 0.8);
  margin: 0;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;
